import React, { useState, useEffect } from 'react';
import { API, Auth, graphqlOperation } from 'aws-amplify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/free-solid-svg-icons'; 

export type StripeStatus = 'free' | 'light' | 'premium'; 

interface TabsProps {
    country: string;
    setCountry: (land: string) => Promise<void>;
    setPublicationIdFilter: (filter: string) => void;
    resetFilters: () => void;
}

const countries = ["NL", "BEL", "DEU", "FRA", "ESP"];

const Tabs: React.FC<TabsProps> = ({
    country,
    setCountry,
    setPublicationIdFilter,
    resetFilters,
}) => {
    const [localStripeStatus, setLocalStripeStatus] = useState<StripeStatus | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchStatus = async () => {
            setIsLoading(true);
            try {
                const currentUser = await Auth.currentAuthenticatedUser();
                const userId = currentUser.attributes.sub;

                const fetchUserQuery = /* GraphQL */ `
                    query GetUserStripeStatus($id: ID!) {
                        getUser(id: $id) {
                            stripeStatus
                        }
                    }
                `;
                const response: any = await API.graphql(graphqlOperation(fetchUserQuery, { id: userId }));
                const status = response.data?.getUser?.stripeStatus as StripeStatus;
                setLocalStripeStatus(status || 'free'); 
            } catch (error) {
                console.error("Error fetching stripeStatus in Tabs:", error);
                setLocalStripeStatus('free'); 
            } finally {
                setIsLoading(false);
            }
        };

        fetchStatus();
    }, []);

    const handleCountryChange = async (land: string) => {
        const isPremiumUser = localStripeStatus === 'premium';
        if (isPremiumUser || land === "NL") {
            handlePublicationIdFilter(land);
            await setCountry(land);
        }
    };

    const handlePublicationIdFilter = (land: string) => {
        if (land === "NL" || land === "") {
            setPublicationIdFilter("-2024");
        } else {
            setPublicationIdFilter("");
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-around mx-6 p-1 space-x-16 animate-pulse">
                {countries.map((land) => (
                    <div key={land} className="h-6 bg-gray-300 rounded w-10"></div>
                ))}
            </div>
        );
    }

    const isPremiumUser = localStripeStatus === 'premium';

    return (
        <div className="flex justify-around mx-6 p-1 space-x-16">
            {countries.map((land) => {
                const isPremiumTab = land !== "NL";
                const isDisabled = isPremiumTab && !isPremiumUser;

                return (
                    <div
                        key={land}
                        className={`group cursor-pointer relative flex justify-center ${country === land ? "text-primary-pink font-bold" : ""} ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`.trim()}
                        onClick={() => {
                            if (!isDisabled) {
                                handleCountryChange(land);
                            }
                        }}
                        aria-label={isPremiumTab ? `${land} (Premium)` : land}
                        role="tab"
                        tabIndex={isDisabled ? -1 : 0}
                    >
                        {land}
                        {/* Premium-indicator: Show if it's a premium tab and user is NOT premium */}
                        {isPremiumTab && !isPremiumUser && (
                            <span
                                className="absolute top-30 left-0 w-full p-2 justify-center items-center scale-0 rounded bg-gray-800 text-xs text-white group-hover:scale-100 group-hover:animate-bounce flex"
                                style={{ width: "6rem" }}
                            >
                                <FontAwesomeIcon
                                    className="mr-3 drop-shadow-xl animate-bounce"
                                    color="#ffd700"
                                    icon="crown"
                                />
                                <p className="font-bold">Premium</p>
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default Tabs;